<template>
  <div>
    <b-row>
      <b-col md="12" class="d-flex flex-wrap align-items-center">
        <feather-icon
          :title="`${$t('actions.go_back')}`"
          class="cursor-pointer"
          icon="ArrowLeftCircleIcon"
          size="25"
          v-b-tooltip.hover
          @click="close"
        />
        <h5 class="mt-1 ml-1">Theme Builder</h5>
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
          <b-form class="d-flex flex-wrap m-2" @submit.prevent="handleSubmit(saveTheme)">
            <validation-provider
              #default="validationContext"
              name="name"
              rules="required"
            >
              <b-form-group
                class="text-capitalize mr-1"
                :label="$t('pbx_master_account.name')"
                label-for="name"
              >
                <b-form-input
                  id="name"
                  v-model="themeName"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              name="tenant"
              rules="required"
            >
              <b-form-group
                label="Tenant"
                label-for="id_tenant"
                :state="getValidationState(validationContext)"
              >
                <v-select
                  data-cy="theme-tenant"
                  v-model="themeIdTenant"
                  :options="tenantOptions"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :clearable="false"
                  :reduce="(val) => val.id"
                  input-id="id_tenant"
                  label="name"
                  class="themeNameInput"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <b-button
              variant="outline-primary"
              size="sm"
              class="ml-2 text-capitalize themeActionsBtns align-self-center"
              @click="close"
            >
              {{ $t("actions.cancel") }}
            </b-button>
            <b-button
              :disabled="loadingSaveTheme"
              type="submit"
              variant="primary"
              size="sm"
              class="ml-1 text-capitalize themeActionsBtns align-self-center"
            >
              {{ isEdit ? $t("actions.edit") : $t("actions.save") }}
            </b-button>
          </b-form>
        </validation-observer>
      </b-col>
    </b-row>

    <b-row>
      <div class="colorCardsContainer">
        <div
          class="colorCard"
          v-for="(color, index) in colors"
          :key="index + color.name"
          ref="cardRefs"
          @click="togglePicker(index)"
        >
          <div class="colorHeader" :style="{ backgroundColor: color.value }">
            <div class="circle"></div>
            <span>{{ color.name }}</span>
          </div>
          <div class="colorInfo">{{ color.value }}</div>
          <b-form-input
            ref="inputColorRefs"
            id="color"
            type="color"
            v-model="color.value"
          />
        </div>
      </div>

      <b-col md="10" class="px-2">
        <div ref="swiperContainer" class="swiper h-100" :style="{ maxWidth: '1200px' }">
          <div class="swiper-wrapper">
          <div class="swiper-slide">
            <h6 class="text-center font-weight-bold">
              {{ $t("account_settings.appColors.main_dashboard") }}
            </h6>
            <div
              class="mockupViewThemeBuilder d-flex flex-column justify-content-center align-items-center"
            >
              <div class="">
                <feather-icon
                  @click="toggleDarkMode"
                  class="cursor-pointer mb-1"
                  size="21"
                  :icon="`${isDark ? 'Sun' : 'Moon'}Icon`"
                  v-b-tooltip.hover.bottom.viewport
                  :title="`${isDark ? $t('light_mode') : $t('dark_mode')}`"
                />
              </div>
              <div class="themeMockupItem mb-1 border">
                <div class="themeMockup">
                  <div
                    class="themeMockupHeader navbar rounded-top"
                    :style="{ backgroundColor: colors.primary.value }"
                  ></div>
                  <div
                    class="themeMockupContent d-flex justify-content-between"
                    :style="{ backgroundColor: getColorOfLayoutMode('third') }"
                  >
                    <div class="leftsidenav w-25 border">
                      <div
                        class="h-25"
                        :style="{ backgroundColor: getColorOfLayoutMode('second') }"
                      ></div>
                    </div>
                    <div
                      class="grid border w-50"
                      :style="{ backgroundColor: getColorOfLayoutMode('main') }"
                    >
                      <div class="grid-box"></div>
                      <div class="grid-box"></div>
                      <div class="grid-box"></div>
                      <div class="grid-box"></div>
                    </div>
                    <div class="rightsidenav border w-25">
                      <div
                        class="h-75"
                        :style="{ backgroundColor: getColorOfLayoutMode('second') }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="swiper-slide">
            <h6 class="text-center font-weight-bold">
              {{ $t("account_settings.appColors.components") }}
            </h6>
            <div class="">
              <span>{{ $t("buttons.title") }}, Badges</span>
              <div class="d-flex flex-wrap themeBuilderButtonsSection">
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.primary.value }"
                >
                  Primary
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.secondary.value }"
                >
                  Secondary
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.success.value }"
                >
                  Success
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.danger.value }"
                >
                  Danger
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.warning.value }"
                >
                  Warning
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.info.value }"
                >
                  Info
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.light.value }"
                >
                  Light
                </button>
                <button
                  type="button"
                  class="btn m-1 border"
                  :style="{ backgroundColor: colors.dark.value }"
                >
                  Dark
                </button>
              </div>
            </div>

            <div class="">
              <span>Table</span>
              <div class="d-flex align-items-center justify-content-center">
                <feather-icon
                  @click="toggleDarkModeComponents"
                  class="cursor-pointer mb-1"
                  size="21"
                  :icon="`${isDarkComponents ? 'Sun' : 'Moon'}Icon`"
                  v-b-tooltip.hover.bottom.viewport
                  :title="`${isDarkComponents ? $t('light_mode') : $t('dark_mode')}`"
                />
              </div>
              <div class="m-2">
                <!-- Table Top -->
                <b-row>
                  <!-- Per Page -->
                  <b-col
                    cols="12"
                    md="6"
                    class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
                  >
                    <label class="text-capitalize">{{ $t("table.show") }}</label>
                    <div
                      class="themeBuilderCustomSelect"
                      :style="{
                        backgroundColor: isDarkComponents ? colors.secondDark.value : '',
                        color: isDarkComponents ? '#fff' : '',
                      }"
                      @click="toggleSelect"
                      ref="themeBuilderSelectRef"
                    >
                      <div class="selected">{{ selected }}</div>
                      <ul
                        :style="{
                          backgroundColor: isDarkComponents ? colors.thirdDark.value : '',
                        }"
                        v-if="selectIsOpen"
                        class="themeBuilderCustomSelectDropdown"
                      >
                        <li
                          v-for="option in [10, 20, 50, 100]"
                          :key="option + 'theme-build-select'"
                          @click.stop="selectOption(option)"
                          :class="{ selected: option === selected }"
                          :style="{
                            backgroundColor:
                              option === selected ? colors.primary.value : '',
                          }"
                        >
                          {{ option }}
                        </li>
                      </ul>
                    </div>
                    <label class="text-capitalize">{{ $t("table.entries") }}</label>
                  </b-col>

                  <!-- Search -->
                  <b-col cols="12" md="6">
                    <div class="d-flex align-items-center justify-content-end">
                      <input
                        :style="{
                          backgroundColor: isDarkComponents
                            ? colors.secondDark.value
                            : '',
                        }"
                        type="text"
                        class="themeBuilderInputText"
                        :placeholder="$t('table.search') + '...'"
                      />
                      <button
                        type="button"
                        class="btn m-1 border text-white"
                        :style="{ backgroundColor: colors.secondary.value }"
                      >
                        <span class="text-nowrap text-capitalize">{{
                          $t("actions.add")
                        }}</span>
                      </button>
                    </div>
                  </b-col>
                </b-row>
              </div>
              <div class="themeBuilderTable">
                <div
                  :style="{
                    backgroundColor: isDarkComponents ? colors.secondDark.value : '',
                    color: isDarkComponents ? '#fff' : '',
                  }"
                  class="themeBuilderTableHeader"
                >
                  <span>name</span>
                  <span>Description</span>
                </div>
                <div
                  class="themeBuilderTableRow"
                  :style="{
                    backgroundColor: isDarkComponents ? colors.mainDark.value : '',
                    color: isDarkComponents ? '#fff' : '',
                  }"
                >
                  <span>test</span>
                  <span>Test description</span>
                </div>
              </div>

              <div class="themeBuilderPagination">
                <div class="backArrow cursor-pointer pagArrows">
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </div>
                <div class="pagItemsContainer">
                  <span
                    @click="updateCustomPage(1)"
                    :style="{
                      backgroundColor:
                        currentPageOnCustomPagination === 1 ? colors.primary.value : '',
                      color: currentPageOnCustomPagination === 1 ? '#fff' : '',
                    }"
                    class="pagItem"
                    >1</span
                  >
                  <span
                    @click="updateCustomPage(2)"
                    :style="{
                      backgroundColor:
                        currentPageOnCustomPagination === 2 ? colors.primary.value : '',
                      color: currentPageOnCustomPagination === 2 ? '#fff' : '',
                    }"
                    class="pagItem"
                    >2</span
                  >
                  <span
                    @click="updateCustomPage(3)"
                    :style="{
                      backgroundColor:
                        currentPageOnCustomPagination === 3 ? colors.primary.value : '',
                      color: currentPageOnCustomPagination === 3 ? '#fff' : '',
                    }"
                    class="pagItem"
                    >3</span
                  >
                </div>
                <div class="nextArrow cursor-pointer pagArrows">
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </div>
              </div>
            </div>
          </div>

          <div class="swiper-slide">
            <h6 class="text-center font-weight-bold">
              {{ $t("account_settings.appColors.images") }}
            </h6>
            <div class="d-flex flex-wrap">
              <div>
                <div class="">Banner logo</div>
                <vue-dropzone
                  id="dropzone-banner-logo"
                  ref="dropzoneImage"
                  :options="dropzoneOptions"
                  @vdropzone-file-added="addLogoImage"
                  @vdropzone-removed-file="removeLogoImage"
                  class="logosImagesDrops"
                />
                <span>Banner logo preview*</span>
                <div
                  class="ThemenavbarLogoPreview navbar rounded-top border"
                  :style="{ backgroundColor: colors.primary.value }"
                >
                  <ul class="nav align-items-center mr-2">
                    <img
                      class="psim-icon"
                      v-if="logoImageFile.url"
                      :src="logoImageFile.url"
                      alt="logo company"
                    />
                    <img
                      class="psim-icon"
                      v-if="isEdit && !logoImageFile.url"
                      :src="appLogoImageDefault"
                      alt="logo company"
                    />
                    <span class="ml-1 text-white textShadowLight">SBO</span>
                  </ul>
                </div>
              </div>
              <div>
                <div class="">Loading logo</div>
                <vue-dropzone
                  id="dropzone-loading-logo"
                  ref="dropzoneImage"
                  :options="dropzoneOptions"
                  @vdropzone-file-added="addLogoLoading"
                  @vdropzone-removed-file="removeLogoLoading"
                  class="logosImagesDrops"
                />
                <span>Loading logo preview*</span>
                <div
                  class="previewLogoLoadingSection border ml-1 d-flex flex-column align-items-center justify-content-center"
                  :style="{ backgroundColor: colors.primary.value }"
                >
                  <img
                    class="psim-icon"
                    v-if="imageLoadingFile.url"
                    :src="imageLoadingFile.url"
                    alt="logo company loading"
                  />
                  <img
                    class="psim-icon"
                    v-if="isEdit && !imageLoadingFile.url"
                    :src="appLogoImageDefault"
                    alt="logo company"
                  />
                  <span class="text-white textShadowLight" v-if="!imageLoadingFile.url"
                    >SBO</span
                  >
                </div>
              </div>
            </div>
          </div>
          </div>
          <div class="swiper-button-prev"></div>
          <div class="swiper-button-next"></div>
          <div class="swiper-pagination"></div>
        </div>
      </b-col>
    </b-row>
    <toastification-loading-content ref="loadingToast" />
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref } from "@vue/composition-api";
import { BButton, BRow, BCol, BCard, BTable, BForm, BFormInput } from "bootstrap-vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { useAppColors } from "@/composables/appColors";
import Pagination from "@/components/Pagination.vue";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import Swiper from "swiper/swiper-bundle";
import "swiper/swiper-bundle.css";

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BCard,
    BTable,
    Pagination,
    vSelect,
    vueDropzone: vue2Dropzone,
    BFormInput,
    BForm,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    ToastificationLoadingContent,
  },
  props: {
    currentTheme: {
      type: Object,
      required: false,
      default: () => {},
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const {
      colors,
      cardRefs,
      inputColorRefs,
      loadingToast,
      loadingSaveTheme,
      isDark,
      dropzoneOptions,
      perPage,
      currentPage,
      logoImageFile,
      imageLoadingFile,
      themeName,
      themeIdTenant,
      tenantOptions,
      isDarkComponents,
      togglePicker,
      handleClickOutside,
      toggleDarkMode,
      getColorOfLayoutMode,
      addLogoImage,
      removeLogoImage,
      addLogoLoading,
      removeLogoLoading,
      saveTheme,
      setEditValues,
      toggleDarkModeComponents,
    } = useAppColors(emit, props.currentTheme, props.isEdit);

    const { refFormObserver, getValidationState, resetForm } = formValidation();
    const appLogoImageDefault = require("@/assets/images/logo/icon-psim.png");
    const themeBuilderSelectRef = ref();
    const selected = ref(10);
    const selectIsOpen = ref(false);
    const currentPageOnCustomPagination = ref(1);
    const swiperContainer = ref(null);
    let swiper = null;

    onMounted(() => {
      if (props.isEdit) setEditValues(props.currentTheme);
      document.addEventListener("click", handleClickOutside);

      swiper = new Swiper(swiperContainer.value, {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
      });
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
      swiper?.destroy(true, true);
    });

    function close() {
      emit("closeThemeBuilder");
    }

    function toggleSelect() {
      selectIsOpen.value = !selectIsOpen.value;
    }

    function selectOption(option) {
      selectIsOpen.value = false;
      selected.value = option;
    }

    function updateCustomPage(option) {
      currentPageOnCustomPagination.value = option;
    }

    return {
      colors,
      cardRefs,
      inputColorRefs,
      isDark,
      perPage,
      currentPage,
      dropzoneOptions,
      logoImageFile,
      imageLoadingFile,
      themeName,
      themeIdTenant,
      tenantOptions,
      refFormObserver,
      loadingToast,
      loadingSaveTheme,
      appLogoImageDefault,
      themeBuilderSelectRef,
      selected,
      selectIsOpen,
      isDarkComponents,
      currentPageOnCustomPagination,
      swiperContainer,
      close,
      togglePicker,
      toggleDarkMode,
      getColorOfLayoutMode,
      addLogoImage,
      removeLogoImage,
      addLogoLoading,
      removeLogoLoading,
      saveTheme,
      getValidationState,
      toggleSelect,
      selectOption,
      toggleDarkModeComponents,
      updateCustomPage,
    };
  },
};
</script>

<style scoped lang="scss">
input[type="color"] {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 0.1px;
  height: 0.1px;
  padding: 0;
  border: none;
  background: transparent;
}
.swiper {
  width: 100%;
  height: 300px;
}
</style>
